import { useMemo } from 'react';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useModuleStore } from '@/stores/module-store';
import { EVENT_APPS } from '@/config/constants';
import { MMM_ALLOWING_SAAS } from '@/types/field-mapping-flow';
import { getIncomingDefaultModule } from '@/config/incoming-default-schema';
import { computeFlowLayout } from '../flow-layout';

/**
 * Hook that converts flow builder store data into React Flow nodes and edges.
 * Recomputes whenever fieldMappings change.
 *
 * For event apps, branches whose event type is disabled in incoming sync
 * settings (ignoredEventTypes) are filtered out of the canvas layout.
 */
export function useFlowLayout() {
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const ignoredEventTypes = useSettingsStore(
    (s) => s.eventIncomingCapture.ignoredEventTypes
  );
  const incomingTargetModule = useSettingsStore(
    (s) => s.incomingCapture.targetModule
  );
  const eventTargetModule = useSettingsStore(
    (s) => s.eventIncomingCapture.targetModule
  );

  // Ignore configuration flags from incoming settings
  const incomingIgnoreNew = useSettingsStore(
    (s) => s.incomingCapture.ignoreNewCustomers
  );
  const eventIgnoreNew = useSettingsStore(
    (s) => s.eventIncomingCapture.ignoreNewCustomers
  );
  const eventIgnoreExisting = useSettingsStore(
    (s) => s.eventIncomingCapture.ignoreExistingCustomers
  );

  const isEventApp = appCodeTPA != null &&
    (EVENT_APPS as readonly string[]).includes(appCodeTPA);

  const appType = isEventApp ? 'events' : 'messaging';

  const triggerLabel = isEventApp
    ? 'When event booked'
    : 'When incoming message received';

  // For event apps, hide branches for disabled (ignored) event types.
  // Data stays in the store so re-enabling an event type restores its config.
  const visibleMappings = useMemo(() => {
    if (!isEventApp || !ignoredEventTypes || ignoredEventTypes.length === 0) {
      return fieldMappings;
    }

    const ignoredSet = new Set(ignoredEventTypes);
    const filtered: Record<string, typeof fieldMappings[string]> = {};

    for (const [docId, mapping] of Object.entries(fieldMappings)) {
      // Branch filterValue format: "eventtype=<eventTypeId>"
      if (mapping.filterValue?.startsWith('eventtype=')) {
        const etId = mapping.filterValue.replace('eventtype=', '');
        if (ignoredSet.has(etId)) continue; // skip disabled event type
      }
      filtered[docId] = mapping;
    }

    return filtered;
  }, [fieldMappings, isEventApp, ignoredEventTypes]);

  const isMultiModule = !!appCodeSAAS && (MMM_ALLOWING_SAAS as readonly string[]).includes(appCodeSAAS);

  const extensionName = useAppStore((s) => s.extensionName);
  const saasModule = useModuleStore((s) => s.saasModule);

  // Determine the default target module. The authoritative source is the
  // server incoming-schema's `defaultEntityModule` (ported into
  // getIncomingDefaultModule), NOT "the first module in the list".
  //   string    → that module is the default (e.g. Freshdesk → "tickets").
  //   null      → the app explicitly has NO default (Monday boards, Airtable
  //               tables) → show "Select a module", never invent one.
  //   undefined → app unknown to the port → fall back to the module list's
  //               own server-marked `selected` flag.
  // User's explicit selection always wins first.
  const incomingModules = saasModule?.getSupportedIncomingModules?.() ?? [];
  const selectedTargetModule = isEventApp ? eventTargetModule : incomingTargetModule;
  const serverSelectedModule = incomingModules.find((m) => m.selected)?.value ?? null;
  const schemaDefaultModule = getIncomingDefaultModule(extensionName ?? undefined);
  const rawTargetModule: string | null =
    selectedTargetModule ||
    (schemaDefaultModule !== undefined ? schemaDefaultModule : serverSelectedModule);
  // Incoming options may be composite values (Pipedrive's "lead_latest_or_create",
  // "person_only") — resolve to the underlying entity module so field-mapping
  // cards are created and labeled with the real module ("lead"), matching how
  // the server looks up syncEntities by card module name. A null result is
  // meaningful (option targets no record module, e.g. "activity_only") — only
  // fall back to the raw value when the module has no resolver.
  const targetModule: string | null =
    rawTargetModule && saasModule?.resolveIncomingEntityModule
      ? saasModule.resolveIncomingEntityModule(rawTargetModule)
      : rawTargetModule;

  const ignoreNewCustomers = isEventApp ? !!eventIgnoreNew : !!incomingIgnoreNew;
  const ignoreExistingCustomers = isEventApp ? !!eventIgnoreExisting : false;

  // Build a label lookup map from SaaS module values (e.g., board IDs) to display names.
  // For Monday.com, module values are board IDs like "4948059996" — this resolves them
  // to human-readable board names like "Test Board". For other SaaS (Zoho, HubSpot, etc.)
  // the values are already human-readable (e.g., "Leads"), so the map is a no-op passthrough.
  const moduleLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    const modules = saasModule?.getSupportedModules?.() || [];
    for (const m of modules) {
      if (m.value && m.label && m.value !== m.label) {
        map[m.value] = m.label;
      }
    }
    return map;
  }, [saasModule]);

  const layout = useMemo(() => {
    return computeFlowLayout({
      fieldMappings: visibleMappings,
      appType: appType as 'messaging' | 'events',
      triggerLabel,
      isMultiModule,
      targetModule,
      moduleLabelMap,
      ignoreNewCustomers,
      ignoreExistingCustomers,
    });
  }, [visibleMappings, appType, triggerLabel, isMultiModule, targetModule, moduleLabelMap, ignoreNewCustomers, ignoreExistingCustomers]);

  return {
    nodes: layout.nodes,
    edges: layout.edges,
    appType: appType as 'messaging' | 'events',
    isEventApp,
  };
}
